import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { Workspace } from "schemas/generated/workspace";
import type { CurrencyCode } from "schemas/project";

import * as React from "react";

import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import {
  Combobox,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { Input } from "@replo/design-system/components/input/Input";
import { Loader } from "@v2/components/Loader";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import { CreateShopModal } from "@v2/features/new-shop-onboarding/CreateShopModal";
import {
  LoadedSettings,
  SettingsLayout,
} from "@v2/features/settings/SettingsCard";
import { DeleteWorkspaceModal } from "@v2/features/settings/shop/modals/DeleteWorkspaceModal";
import { useUpdateWorkspace } from "@v2/features/settings/workspace/useUpdateWorkspace";
import { useIsWorkspaceOwner } from "@v2/hooks/useIsWorkspaceOwner";
import { projectRouter } from "@v2/trpc/routes/project";
import { getCurrencySymbol } from "@v2/utils/currency";
import { getProjectLabels } from "@v2/utils/project-labels";
import { Clock } from "lucide-react";
import { useForm } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { formatTimezoneDetailed } from "replo-utils/lib/timezone";
import { CURRENCIES } from "schemas/project";

export type WorkspaceSettings = Pick<
  Workspace,
  "name" | "timeZone" | "currency"
>;

// Helper hook to determine context based on URL
const useSettingsContext = () => {
  const location = useLocation();
  const isShopContext = location.pathname.includes("/shops/");
  return { isShopContext };
};

const WorkspaceSettingsForm: React.FC<{
  workspace: Workspace;
  isShopContext: boolean;
}> = ({ workspace, isShopContext }) => {
  const { updateWorkspace } = useUpdateWorkspace();
  const {
    handleSubmit,
    setValue,
    formState: { isDirty },
    watch,
    reset,
  } = useForm<WorkspaceSettings>({
    defaultValues: {
      name: workspace.name,
      timeZone:
        workspace.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      currency: workspace.currency ?? "USD",
    },
  });

  const handleSetValue = (
    key: keyof WorkspaceSettings,
    value: WorkspaceSettings[keyof WorkspaceSettings],
  ) => {
    setValue(key, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleWorkspaceUpdate = async (
    workspaceSettings: WorkspaceSettings,
  ) => {
    await updateWorkspace({
      id: workspace.id,
      data: workspaceSettings,
    });
    reset({}, { keepValues: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <NameInput
        initialName={watch("name")}
        onChange={(value) => handleSetValue("name", value)}
      />
      {/* NOTE (Kurt, 2025-06-04): We only show timezone and currency if NOT in shop context. */}
      {!isShopContext && (
        <>
          <TimeZoneSettings
            timeZone={watch("timeZone") ?? ""}
            onChange={(value) => handleSetValue("timeZone", value)}
          />
          <CurrencySettings
            currency={watch("currency") ?? "USD"}
            onChange={(value) => handleSetValue("currency", value)}
          />
        </>
      )}
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="primary"
          size="base"
          onClick={() => void handleSubmit(handleWorkspaceUpdate)()}
          disabled={!isDirty}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const TimeZoneSettings: React.FC<{
  timeZone: string;
  onChange: (value: string) => void;
}> = ({ timeZone, onChange }) => {
  const timezoneOptions = Intl.supportedValuesOf("timeZone").map(
    (timezone) => ({
      label: formatTimezoneDetailed(timezone),
      value: timezone,
    }),
  );

  timezoneOptions.unshift({
    label: formatTimezoneDetailed("UTC"),
    value: "UTC",
  });

  const items: ComboboxItemWithValue[] = timezoneOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  const selectedItem = items.find((item) => item.value === timeZone) ?? null;

  return (
    <LabeledControl
      label="Timezone"
      description="All analytics will be reported using this timezone."
      size="base"
    >
      <Combobox
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          if (item) {
            onChange(item.value);
          }
        }}
        isItemEqualToValue={(a, b) => a.value === b.value}
        trigger={
          <ComboboxTriggerButton
            size="base"
            startEnhancer={<Clock size={14} />}
            isPlaceholder={!Boolean(timeZone)}
          >
            {formatTimezoneDetailed(timeZone ?? "")}
          </ComboboxTriggerButton>
        }
        triggerAsChild
        placeholder="Search..."
        UNSAFE_className="w-[300px]"
      />
    </LabeledControl>
  );
};

const CurrencySettings: React.FC<{
  currency: CurrencyCode;
  onChange: (value: string) => void;
}> = ({ currency, onChange }) => {
  const formatCurrencyLabel = (code: CurrencyCode, name: string) => {
    const symbol = getCurrencySymbol(code);
    // NOTE (Kurt, 2025-04-02): Some currency symbols aren't rendered consistently in the browser, so the
    // Intl API returns the currency code instead, if we get a code back we don't want to show it since we
    // already show the currency code in the label.
    return `${code} - ${name}${symbol !== code ? ` (${symbol})` : ""}`;
  };

  const currencyOptions = Object.entries(CURRENCIES).map(
    ([code, { name }]) => ({
      label: formatCurrencyLabel(code as CurrencyCode, name),
      value: code,
    }),
  );

  const items: ComboboxItemWithValue[] = currencyOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  const selectedItem = items.find((item) => item.value === currency) ?? null;

  return (
    <LabeledControl
      label="Currency"
      description="All analytics will be reported in this currency."
      size="base"
    >
      <Combobox
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          if (item) {
            onChange(item.value);
          }
        }}
        isItemEqualToValue={(a, b) => a.value === b.value}
        trigger={
          <ComboboxTriggerButton size="base" isPlaceholder={!Boolean(currency)}>
            {formatCurrencyLabel(currency, CURRENCIES[currency].name)}
          </ComboboxTriggerButton>
        }
        triggerAsChild
        placeholder="Search..."
        UNSAFE_className="w-[300px]"
      />
    </LabeledControl>
  );
};

const NameInput = ({
  initialName,
  onChange,
}: {
  initialName: string;
  onChange: (value: string) => void;
}) => {
  return (
    <LabeledControl label="Workspace Name" size="base">
      <Input
        value={initialName}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your workspace name"
        size="base"
      />
    </LabeledControl>
  );
};

type WorkspaceDeleteSectionProps = {
  workspaceId: string;
};

const WorkspaceDeleteSection: React.FC<WorkspaceDeleteSectionProps> = ({
  workspaceId,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  return (
    <div className="flex flex-col gap-4">
      <Alert
        variant="critical"
        description="This action permanently deletes your workspace and all its data. Recovery requires contacting Replo support."
      />
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="destructive"
          size="base"
          onClick={() => {
            setIsDeleteModalOpen(true);
          }}
        >
          Delete Workspace
        </Button>
      </div>
      <DeleteWorkspaceModal
        workspaceId={workspaceId}
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
      />
    </div>
  );
};

const WorkspaceSettingsWrapper = () => {
  const { isShopContext } = useSettingsContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspace, isLoading: isLoadingWorkspace } =
    useCurrentWorkspaceContext();

  const workspaceId = workspace?.id;
  const { data: projectList, isLoading: isLoadingWorkspaceProjectData } =
    projectRouter.listWithStats.useQuery({ workspaceId });

  const userIsOwner = useIsWorkspaceOwner(workspaceId);
  const currentProjectContext = useCurrentProjectContext();
  const { userRole } = currentProjectContext ?? {};
  const projectLabels = getProjectLabels();

  // In workspace-only mode, use workspace owner status; otherwise use project context
  const canDeleteWorkspace = !currentProjectContext?.project
    ? userIsOwner
    : exhaustiveSwitch({ type: userRole ?? "none" })({
        superuser: () => true,
        workspaceOwner: () => true,
        workspaceMember: () => false,
        projectMember: () => false,
        none: () => false,
      });

  const isLoading = isLoadingWorkspace || isLoadingWorkspaceProjectData;

  const newShop = searchParams.get("new-shop");
  const createShopModalProps =
    newShop && workspaceId
      ? {
          workspaceId,
          title: `Add a ${projectLabels.singularCapitalized} to Your New Workspace`,
        }
      : null;

  if (!workspace || !projectList) {
    return null;
  }

  const sections = [
    {
      title: "Workspace Settings",
      component: (
        <WorkspaceSettingsForm
          workspace={workspace}
          isShopContext={isShopContext}
        />
      ),
    },
  ];

  if ((isShopContext && canDeleteWorkspace) || !isShopContext) {
    sections.push({
      title: "Delete Workspace",
      component: <WorkspaceDeleteSection workspaceId={workspace.id} />,
    });
  }

  return (
    <div className="flex flex-col flex-1 gap-4 px-6">
      <SettingsLayout>
        {isLoading ? <Loader /> : <LoadedSettings sections={sections} />}
      </SettingsLayout>
      {createShopModalProps && (
        <CreateShopModal
          open
          onOpenChange={(open) => {
            if (!open) {
              setSearchParams((params) => {
                params.delete("new-shop");
                return params;
              });
            }
          }}
          {...createShopModalProps}
        />
      )}
    </div>
  );
};

export default WorkspaceSettingsWrapper;
